import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import JitsiJoinModal from './JitsiJoinModal';
import { getJitsiRoomName, getPreferredJoinURL } from '../utils/jitsiHelper';
import './StudentOnlineClassesView.css';

function StudentOnlineClassesView({ student }) {
  const [onlineClasses, setOnlineClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(0); // 0: Today, 1: Upcoming, 2: Previous
  const [jitsiModal, setJitsiModal] = useState({ isOpen: false, roomName: '', displayName: '' });

  const loadOnlineClasses = useCallback(async () => {
    try {
      setLoading(true);
      setOnlineClasses([]);

      const classId = student.classId;
      if (!classId) {
        console.log('No classId for student:', classId);
        setLoading(false);
        return;
      }

      const onlineClassesRef = collection(db, 'classes', classId, 'onlineClasses');
      const snapshot = await getDocs(onlineClassesRef);
      const classes = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Sort by date (newest first)
      classes.sort((a, b) => new Date(b.date) - new Date(a.date));
      setOnlineClasses(classes);
    } catch (error) {
      console.error('Error loading online classes:', error);
    } finally {
      setLoading(false);
    }
  }, [student.classId]);

  useEffect(() => {
    loadOnlineClasses();
  }, [loadOnlineClasses]);

  const handleJoinClass = (onlineClass) => {
    const joinURL = getPreferredJoinURL(onlineClass);
    if (!joinURL) {
      alert('This class is not joinable at this time.');
      return;
    }

    if (!joinURL.includes('meet.jit.si')) {
      window.open(joinURL, '_blank');
      return;
    }

    const roomName = getJitsiRoomName(onlineClass.id);
    const displayName = student.fullName || 'Student';
    setJitsiModal({ isOpen: true, roomName, displayName });
  };

  const closeJitsiModal = () => {
    setJitsiModal({ isOpen: false, roomName: '', displayName: '' });
  };

  const canJoin = (onlineClass) => {
    const isOnline = onlineClass.classMode === 'Online' || onlineClass.classMode === 'Hybrid';
    const isActive = onlineClass.status === 'Active' || onlineClass.status === 'active';
    const now = new Date();
    const classDate = new Date(onlineClass.date);
    const isToday = classDate.toDateString() === now.toDateString();
    return isOnline && (isActive || isToday) && getPreferredJoinURL(onlineClass);
  };

  const todaysClasses = onlineClasses.filter(c => {
    const classDate = new Date(c.date);
    const today = new Date();
    return classDate.toDateString() === today.toDateString();
  });

  const upcomingClasses = onlineClasses.filter(c => {
    const classDate = new Date(c.date);
    const today = new Date();
    return classDate >= today && c.status !== 'Cancelled' && c.status !== 'cancelled';
  });

  const previousClasses = onlineClasses.filter(c => {
    const classDate = new Date(c.date);
    const today = new Date();
    return c.status === 'Completed' || c.status === 'completed' || classDate < today;
  });

  const tabs = [
    { label: 'Today', count: todaysClasses.length },
    { label: 'Upcoming', count: upcomingClasses.length },
    { label: 'Previous', count: previousClasses.length },
  ];

  const getClassesForTab = () => {
    switch (selectedTab) {
      case 0: return todaysClasses;
      case 1: return upcomingClasses;
      case 2: return previousClasses;
      default: return [];
    }
  };

  const currentClasses = getClassesForTab();

  return (
    <div className="student-online-classes-view">
      <h2>Online Classes</h2>

      {loading ? (
        <div className="loading">Loading online classes...</div>
      ) : onlineClasses.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📹</div>
          <h3>No Online Classes</h3>
          <p>Online classes scheduled by your teachers will appear here.</p>
        </div>
      ) : (
        <>
          <div className="tabs-container">
            {tabs.map((tab, index) => (
              <button
                key={index}
                className={`tab-button ${selectedTab === index ? 'active' : ''}`}
                onClick={() => setSelectedTab(index)}
              >
                {tab.label}
                {tab.count > 0 && <span className="tab-badge">{tab.count}</span>}
              </button>
            ))}
          </div>

          <div className="classes-list">
            {currentClasses.length === 0 ? (
              <div className="no-classes-tab">
                <p>No classes in this tab</p>
              </div>
            ) : (
              currentClasses.map(onlineClass => (
                <div key={onlineClass.id} className="class-card">
                  <div className="class-header">
                    <span className={`class-mode ${onlineClass.classMode?.toLowerCase()}`}>
                      {onlineClass.classMode === 'Online' ? '📹' : onlineClass.classMode === 'Hybrid' ? '🔄' : '🏫'}
                    </span>
                    <span className={`class-status ${onlineClass.status?.toLowerCase()}`}>
                      {onlineClass.status}
                    </span>
                  </div>
                  <h3>{onlineClass.subjectName || 'Class'}</h3>
                  <p className="topic">{onlineClass.topic || 'No topic'}</p>
                  <div className="class-meta">
                    <span>👨‍🏫 {onlineClass.teacherName || 'Teacher'}</span>
                    <span>📅 {new Date(onlineClass.date).toLocaleDateString()}</span>
                    <span>⏰ {onlineClass.startTime} - {onlineClass.endTime}</span>
                  </div>
                  {canJoin(onlineClass) && (
                    <button className="join-button" onClick={() => handleJoinClass(onlineClass)}>
                      Join Class
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </>
      )}

      <JitsiJoinModal
        isOpen={jitsiModal.isOpen}
        roomName={jitsiModal.roomName}
        displayName={jitsiModal.displayName}
        onClose={closeJitsiModal}
      />
    </div>
  );
}

export default StudentOnlineClassesView;
